import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { syncAuthFromStorage, useAuthStore } from '@/stores/auth';
import { authApi } from '@/api/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { OwnerEntity } from '@bhops/shared';
import { GlobalSearchBar } from '@/components/GlobalSearchBar';
import { NotificationBell } from '@/components/NotificationBell';
import { LocaleSwitcher } from '@/components/LocaleSwitcher';
import { PatchNotesButton } from '@/components/PatchNotesButton';
import { TweaksPanel } from '@/components/TweaksPanel';
import { ChangePasswordModal } from '@/components/ChangePasswordModal';
import { Icon } from '@/components/Icon';
import { useIsMobile } from '@/lib/responsive';
import { SECTION_ACCENT, sectionFill, sectionForPath } from './section-accent';

interface Props {
  title: string;
  subtitle?: string;
  // 모바일에서 사이드바 드로어를 여는 햄버거. 데스크톱은 undefined(버튼 숨김).
  onMenuClick?: () => void;
}

export function TopBar({ title, subtitle, onMenuClick }: Props) {
  const isMobile = useIsMobile();
  const intl = useIntl();
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // 2026-10-21 오너 — 상단 바는 지금 화면이 속한 사이드바 묶음의 색(홈 등 묶음 밖은 기본색).
  const section = sectionForPath(useLocation().pathname);
  const fill = section ? sectionFill(SECTION_ACCENT[section]) : null;
  // windows-ux-edit (2026-06, 슬라이드2) — 자가 비밀번호 변경 모달 + 사용자 메뉴 드롭다운.
  const [pwOpen, setPwOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    function onDocMouseDown(e: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [menuOpen]);

  const OWNER_LABEL: Record<OwnerEntity, string> = {
    [OwnerEntity.HONEYCOMB_ROBOTICS]: intl.formatMessage({ id: 'owner.honeycomb_robotics' }),
    [OwnerEntity.BEYOND_HONEYCOMB]: intl.formatMessage({ id: 'owner.beyond_honeycomb' }),
  };

  const ROLE_LABEL: Record<string, string> = {
    ADMIN: intl.formatMessage({ id: 'role.admin' }),
    MANAGER: intl.formatMessage({ id: 'role.manager' }),
    SALES_MANAGER: intl.formatMessage({ id: 'role.sales_manager' }),
    MFG_MANAGER: intl.formatMessage({ id: 'role.mfg_manager' }),
    FIELD_MANAGER: intl.formatMessage({ id: 'role.field_manager' }),
    VIEWER: intl.formatMessage({ id: 'role.viewer' }),
  };

  async function handleLogout(): Promise<void> {
    try {
      // 갱신 토큰은 탭 공용이라 다른 탭이 방금 바꿨을 수 있다 — 저장소의 최신 것으로 끊는다.
      await syncAuthFromStorage();
      const refreshToken = useAuthStore.getState().refreshToken;
      if (refreshToken) await authApi.logout(refreshToken);
    } catch {
      // swallow — clear local state regardless so the user isn't stuck.
    }
    clear();
    // round10 B3 리뷰 — 공유 단말에서 다음 사용자가 이전 사용자의 캐시(수익 금액·고객
    // PII 등)를 물려받지 않도록 로그아웃 시 react-query 캐시 전체 비운다.
    queryClient.clear();
    navigate('/login', { replace: true });
  }

  const wrap: CSSProperties = {
    height: 60,
    borderBottom: fill ? `1px solid ${fill}` : '1px solid var(--border-default)',
    background: fill ?? 'var(--bg-surface)',
    ...(fill ? { color: '#fff' } : {}),
    display: 'flex',
    alignItems: 'center',
    padding: isMobile ? '0 12px' : '0 22px',
    gap: isMobile ? 8 : 16,
    flexShrink: 0,
  };

  const initial = user?.displayName?.[0] ?? user?.email?.[0]?.toUpperCase() ?? '?';

  const userMenu: CSSProperties = {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    right: 0,
    minWidth: 160,
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-sm)',
    boxShadow: 'var(--shadow-md)',
    padding: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    zIndex: 50,
  };
  const userMenuItem: CSSProperties = {
    textAlign: 'left',
    border: 'none',
    background: 'transparent',
    color: 'var(--fg-default)',
    padding: '8px 10px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 13,
    cursor: 'pointer',
  };

  return (
    <header style={wrap} className={fill ? 'kuun-no-select kuun-topbar-tint' : 'kuun-no-select'}>
      {/* 모바일 햄버거 — 사이드바 드로어를 연다(데스크톱은 렌더 안 함). */}
      {onMenuClick && (
        <button
          type="button"
          aria-label="메뉴 열기"
          onClick={onMenuClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            flexShrink: 0,
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            background: 'transparent',
            color: 'inherit',
            cursor: 'pointer',
          }}
        >
          <Icon name="menu" size={22} />
        </button>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <div
          style={{
            // 2026-10-21 오너 — 묶음 색 바 위 제목을 더 크게(19 → 23).
            fontSize: isMobile ? 18 : 23,
            fontWeight: 800,
            letterSpacing: '-0.01em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </div>
        {subtitle && !isMobile && (
          <div style={{ fontSize: 12, color: fill ? 'rgba(255,255,255,0.8)' : 'var(--fg-muted)' }}>
            {subtitle}
          </div>
        )}
      </div>

      {/* 검색바는 데스크톱만 인라인 노출(모바일은 폭이 없어 숨김 — 1단계는 넘침 방지가 목표). */}
      {!isMobile && (
        <div style={{ marginLeft: 'auto' }}>
          <GlobalSearchBar accent={section ? SECTION_ACCENT[section] : undefined} />
        </div>
      )}

      {/* windows-ux-edit (2026-06, 슬라이드2) — 상단 'API' 배지 제거.
          (헬스는 사이드바 footer 점 + /api/v1/health 로 확인.) */}
      <div
        style={isMobile ? { marginLeft: 'auto', display: 'flex', alignItems: 'center' } : undefined}
      >
        <NotificationBell />
      </div>

      {/* §⑤ Phase 5 — Tweaks (다크 모드 / 정보 밀도). localStorage 저장. */}
      <TweaksPanel />

      {/* 2026-10-19 — 패치노트(새 업데이트면 깜빡임). 모바일은 아이콘만. */}
      <PatchNotesButton iconOnly={isMobile} />

      {/* 로케일 스위처는 모바일에서 공간 절약을 위해 숨긴다(설정은 데스크톱에서). */}
      {!isMobile && <LocaleSwitcher />}

      {/* windows-ux-edit (2026-06) — 사용자 정보 칩을 클릭하면 메뉴(비밀번호 변경 / 로그아웃). */}
      <div ref={menuRef} style={{ position: 'relative' }}>
        <button
          type="button"
          aria-label="사용자 메뉴"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '4px 10px 4px 4px',
            border: 'none',
            borderRadius: 999,
            // 묶음 색 상단 바 위에서는 옆 버튼들과 같은 흰 반투명.
            background: fill ? 'rgba(255,255,255,0.14)' : 'var(--bg-sunken)',
            boxShadow: fill ? 'inset 0 0 0 1px rgba(255,255,255,0.42)' : undefined,
            cursor: 'pointer',
            color: 'inherit',
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'var(--brand)',
              color: 'var(--brand-on)',
              fontSize: 13,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {initial}
          </div>
          {/* 모바일: 아바타만 남기고 이름·역할 텍스트는 숨긴다(탭하면 메뉴에서 확인). */}
          {!isMobile && (
            <div style={{ lineHeight: 1.2, textAlign: 'left' }}>
              <div style={{ fontSize: 12, color: fill ? '#fff' : 'var(--fg-default)' }}>
                {user?.displayName ?? user?.email}
              </div>
              <div
                style={{ fontSize: 10, color: fill ? 'rgba(255,255,255,0.8)' : 'var(--fg-muted)' }}
              >
                {user && `${ROLE_LABEL[user.role] ?? user.role} · ${OWNER_LABEL[user.ownerEntity]}`}
              </div>
            </div>
          )}
        </button>

        {menuOpen && (
          <div role="menu" style={userMenu}>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                setPwOpen(true);
              }}
              style={userMenuItem}
            >
              비밀번호 변경
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void handleLogout();
              }}
              style={userMenuItem}
            >
              {intl.formatMessage({ id: 'topbar.logout' })}
            </button>
          </div>
        )}
      </div>

      {pwOpen && <ChangePasswordModal open onClose={() => setPwOpen(false)} />}
    </header>
  );
}
